/**
 * "Who we've asked on this case" (proposal 2, kanban 31f2809d). Nothing on a
 * case said who was sent what: the asks sat far down inside checklist rows,
 * an ask only said OPEN after Send, delivery (sandbox, Slack failed, the
 * portal card) was only knowable by asking Merlin, and items "on a
 * colleague" named nobody and had never actually asked anyone.
 *
 * One row per ask that went out: who, what, when, how it reached them on
 * each medium, whether they answered (and whether the answer is read), when
 * the next reminder goes, and how overdue it is in words. Checklist items
 * nobody was asked about are asked from the item itself (the rail's "Never
 * asked" rows were merged into it, UX round 2026-11-03).
 *
 * Pure logic (repo convention: logic in .ts, components in .tsx).
 */
import type {
  AskDeliveryChannel,
  DoorAskDeliveryRead,
  DoorDataRequestRead,
} from '../../services/qcpDataRequests';
import { answeredAtLong, askedOn } from './answersView';
import { isDraft, isPassAlong, isWaitingAsk } from '../../components/quality/askKind';

export type AskReply = 'waiting' | 'handed' | 'answered' | 'unread';

export interface AskRailRow {
  request: DoorDataRequestRead;
  /** Who was asked, as the record names them. */
  person: string;
  /** What they were asked. */
  question: string;
  /** When it went out (ISO); null when unknown. */
  sentAt: string | null;
  deliveries: DoorAskDeliveryRead[];
  reply: AskReply;
  /** When the next automatic reminder goes (ISO); null when none is due. */
  reminderAt: string | null;
  /** When it should have been answered by: the reminder that is due, else
   *  two days after the last send (ISO); null once answered. */
  chaseBy: string | null;
  /** "Overdue 2 days" once a full day past the chase-by; else null. */
  overdueWords: string | null;
}

export interface AskRail {
  rows: AskRailRow[];
  /** Merlin's drafts waiting for a Send (UX round 2026-10-13: Merlin said
   *  "drafted, press Send" while this panel said nobody had been asked).
   *  Do next step drafts count too (UX round 2026-10-20: "Nobody has been
   *  asked" sat above three of them); their Review button goes to Do next. */
  drafts: DoorDataRequestRead[];
  /** Asks that came back with an answer, newest first. */
  answers: DoorDataRequestRead[];
}

const DAY_MS = 24 * 60 * 60 * 1000;
/** The follow-up sweep's own interval (data_request_followup_interval_hours). */
const CHASE_AFTER_MS = 2 * DAY_MS;

function ms(iso: string | null | undefined): number {
  const t = iso ? new Date(iso).getTime() : NaN;
  return Number.isNaN(t) ? NaN : t;
}

function replyOf(r: DoorDataRequestRead): AskReply {
  if (r.status === 'answered' || r.status === 'closed') return r.answer_unseen ? 'unread' : 'answered';
  // A pass-along (kanban 196e9d9b) is theirs now; nobody chases a reply.
  if (isPassAlong(r)) return 'handed';
  return 'waiting';
}

function chaseByOf(r: DoorDataRequestRead): string | null {
  if (r.status !== 'open' || isPassAlong(r)) return null;
  if (r.next_followup_at) return r.next_followup_at;
  // Reminders exhausted (or never scheduled): due two days after the last send.
  const last = ms(r.last_followup_at ?? r.sent_at ?? r.created_at);
  return Number.isNaN(last) ? null : new Date(last + CHASE_AFTER_MS).toISOString();
}

/** "Overdue 2 days" once at least a whole day has passed since `chaseBy`. */
export function overdueWords(chaseBy: string | null, now: Date): string | null {
  const due = ms(chaseBy);
  if (Number.isNaN(due)) return null;
  const days = Math.floor((now.getTime() - due) / DAY_MS);
  if (days < 1) return null;
  return `Overdue ${days} day${days === 1 ? '' : 's'}`;
}

function rowFor(r: DoorDataRequestRead, now: Date): AskRailRow {
  const chaseBy = chaseByOf(r);
  const next = ms(r.next_followup_at);
  return {
    request: r,
    person: r.target_name,
    question: r.question_title,
    sentAt: r.sent_at ?? r.created_at ?? null,
    deliveries: r.deliveries ?? [],
    reply: replyOf(r),
    reminderAt: isWaitingAsk(r) && !Number.isNaN(next) && next > now.getTime() ? r.next_followup_at : null,
    chaseBy,
    overdueWords: overdueWords(chaseBy, now),
  };
}

/** Overdue first (most overdue on top), then waiting (oldest first), then
 *  answered (unread first, newest first). */
function byRail(a: AskRailRow, b: AskRailRow): number {
  const rank = (row: AskRailRow) =>
    row.overdueWords ? 0 : row.reply === 'waiting' ? 1 : row.reply === 'unread' ? 2 : row.reply === 'handed' ? 3 : 4;
  const d = rank(a) - rank(b);
  if (d !== 0) return d;
  if (rank(a) <= 1) return (ms(a.chaseBy ?? a.sentAt) || 0) - (ms(b.chaseBy ?? b.sentAt) || 0);
  return (ms(b.request.answered_at) || 0) - (ms(a.request.answered_at) || 0);
}

/** The case's asks as the rail shows them. `asks` is every ask on the case
 *  (asksForCase); withdrawn asks are not rows, drafts are listed apart. */
export function askRail(asks: DoorDataRequestRead[], now: Date = new Date()): AskRail {
  // A pass-along held for Merlin's check is `proposed` but is not a draft:
  // it went out from the sender's side (kanban 196e9d9b).
  const went = asks.filter((r) => !isDraft(r) && r.status !== 'cancelled');
  const rows = went.map((r) => rowFor(r, now)).sort(byRail);
  const drafts = asks
    .filter(isDraft)
    .sort((a, b) => a.created_at.localeCompare(b.created_at));
  const answers = went
    .filter((r) => r.status === 'answered' || r.status === 'closed')
    .sort((a, b) => (ms(b.answered_at) || 0) - (ms(a.answered_at) || 0));
  return { rows, drafts, answers };
}

/** Does the "Who we've asked" panel list an ask that went out? Then the
 *  panel leads the rail and Merlin's chat starts folded (proposal 2). Its
 *  "Never asked" rows were merged into the checklist item's own Ask (UX
 *  round 2026-11-03), so a checklist no longer counts here. */
export function railHasRows(asks: DoorDataRequestRead[]): boolean {
  return askRail(asks).rows.length > 0;
}

/** How many asks on the rail are overdue (the header's pill). */
export function overdueCount(rows: AskRailRow[]): number {
  return rows.filter((r) => r.overdueWords !== null).length;
}

const CHANNEL_WORD: Record<AskDeliveryChannel, string> = {
  email: 'email',
  slack: 'Slack',
  widget: 'portal',
  sms: 'text',
};

/** One medium in words: "email ✓ delivered", "Slack ✗ failed", "portal ✓". */
export function deliveryWord(d: Pick<DoorAskDeliveryRead, 'channel' | 'state'>): string {
  const name = CHANNEL_WORD[d.channel] ?? d.channel;
  switch (d.state) {
    case 'sent':
      return d.channel === 'email' ? `${name} ✓ delivered` : `${name} ✓`;
    case 'sandbox':
      return `${name} ✓ test address (sandbox)`;
    case 'failed':
      return `${name} ✗ failed`;
    default:
      // Approved, nothing has crossed this medium yet (delivery_status.py:
      // a binding with no outbound message). "not sent yet" beside "✓ Sent"
      // read as a contradiction (UX round 2026-10-20).
      return `${name} … queued`;
  }
}

/** "email ✓ delivered · Slack ✗ failed · portal ✓"; null when nothing is known. */
export function deliveryLine(deliveries: DoorAskDeliveryRead[]): string | null {
  if (deliveries.length === 0) return null;
  return deliveries.map(deliveryWord).join(' · ');
}

/** "✓ Sent today 8:06 PM" / "✓ Sent Oct 6 2:15 PM"; "Sending since …"
 *  while every medium it goes by is still queued (Send was pressed, nothing
 *  has gone out yet). */
export function sentWords(
  row: Pick<AskRailRow, 'sentAt'> & Partial<Pick<AskRailRow, 'deliveries'>>,
  now: Date = new Date(),
): string {
  const when = row.sentAt ? answeredAtLong(row.sentAt, now) : '';
  const deliveries = row.deliveries ?? [];
  const queued = deliveries.length > 0 && deliveries.every((d) => d.state === 'pending');
  if (queued) return when ? `Sending since ${when.replace(/^today /, '')}` : 'Sending';
  return when ? `✓ Sent ${when}` : '✓ Sent';
}

/** "Mon Oct 12": the next reminder's day. */
export function reminderDay(at: string): string {
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).replace(',', '');
}

/** The muted line under a row: what is pending and when.
 *  waiting: "No answer yet · reminder Mon Oct 12"
 *  overdue: "asked Oct 6 · chase-by Oct 8"
 *  answered: "Answered Oct 8" */
export function rowNote(row: AskRailRow): string {
  if (row.reply === 'handed') {
    // A pass-along (kanban 196e9d9b): theirs now; say when it went and that
    // nobody here waits on it.
    return row.sentAt ? `Passed along ${askedOn(row.sentAt)} · nothing waiting on you` : 'Passed along · nothing waiting on you';
  }
  if (row.reply !== 'waiting') {
    const r = row.request;
    if (r.closed_in_chat) {
      // The user told Merlin the answer; Merlin closed the ask (kanban 101a9853).
      const who = r.closed_by_label || 'Merlin';
      const when = r.closed_at ? ` ${askedOn(r.closed_at)}` : '';
      const note = (r.closed_note ?? '').trim();
      return `Closed from chat by ${who}${when}${note ? `: ${note}` : ''}`;
    }
    return r.answered_at ? `Answered ${askedOn(r.answered_at)}` : 'Answered';
  }
  if (row.overdueWords) {
    const parts = [row.sentAt ? `asked ${askedOn(row.sentAt)}` : '', row.chaseBy ? `chase-by ${askedOn(row.chaseBy)}` : ''];
    return parts.filter(Boolean).join(' · ');
  }
  return row.reminderAt ? `No answer yet · reminder ${reminderDay(row.reminderAt)}` : 'No answer yet';
}
